import { Moment } from 'moment';

import { UNIVERSES } from '../constants/universes';
import {
  BasketContent,
  BasketContentItem,
  BasketPrices,
  CleaningPrestation,
  CleaningPrestationOption,
  ComboPrice,
  GiftCard,
  Promotion,
  Schedule,
  Universe,
  YoungestChildAge,
  ZoneId,
} from '../types/commons';
import { getAllPrestationsForCategory } from '../utils/prestations';

import { computeCleaningPrices, computeNewCleaningPrices } from './cleaning-prices';
import { basketContentPrices, getPrestationPriceFromUniverseCategory } from './common-prices';
import { computeChildcarePrices } from './childcare-prices';
import { computeWellnessPrices } from './wellness-prices';

type GetCategoryPrestationsPricesParams = {
  basketCategory: BasketContentItem;
  universe: Universe;
  zone: ZoneId | null;
  comboPrices: ComboPrice[];
  membershipComboPrices: ComboPrice[];
};

export const getCategoryPrestationsPrices = ({
  basketCategory,
  universe,
  zone,
  comboPrices,
  membershipComboPrices,
}: GetCategoryPrestationsPricesParams) => {
  const universeCategoryPrestations = getAllPrestationsForCategory(basketCategory.reference, universe);

  return universeCategoryPrestations.reduce(
    (acc, prestation) => ({
      ...acc,
      [prestation.reference]: getPrestationPriceFromUniverseCategory(
        universeCategoryPrestations,
        prestation.reference,
        basketCategory.prestations,
        comboPrices,
        zone,
        membershipComboPrices,
      ),
    }),
    {},
  );
};

type ComputePricesParams = {
  universe: Universe;
  primaryAppointment: Moment | null;
  basketContent: BasketContent;
  cleaningPrestation?: CleaningPrestation;
  cleaningPrestationOptions?: CleaningPrestationOption[];
  promotion?: Promotion | null;
  giftCard?: GiftCard;
  minPriceSurchargeAccepted: boolean;
  duration?: number | null;
  zone: ZoneId;
  comboPrices: ComboPrice[];
  schedules?: Schedule[];
  childcareHoursPerWeek?: number;
  includeHolidays?: boolean;
  allowanceAmount?: number;
  tipAmount?: number;
  youngestChildAge?: YoungestChildAge;
  membershipComboPrices: ComboPrice[];
};

export const computePrices = ({
  universe,
  primaryAppointment,
  basketContent,
  cleaningPrestation,
  cleaningPrestationOptions,
  promotion,
  giftCard,
  minPriceSurchargeAccepted,
  duration,
  zone,
  comboPrices,
  schedules,
  childcareHoursPerWeek,
  includeHolidays,
  allowanceAmount,
  tipAmount,
  youngestChildAge,
  membershipComboPrices,
}: ComputePricesParams): BasketPrices => {
  switch (universe.reference) {
    case UNIVERSES.CHILDCARE:
      return computeChildcarePrices({
        universe,
        primaryAppointment,
        basketContent,
        zone,
        schedules,
        childcareHoursPerWeek,
        includeHolidays,
        allowanceAmount,
        tipAmount,
        duration,
        promotion,
        giftCard,
        youngestChildAge,
      });
    case UNIVERSES.CLEANING:
      // This is the new way of storing the prestations inside the basket for cleaning. Here, if we have the
      // cleaningPrestation inside the basket we redirect to the new calculation price otherwise, same as before
      if (cleaningPrestation) {
        return computeNewCleaningPrices({
          universe,
          duration,
          cleaningPrestation,
          cleaningPrestationOptions,
          primaryAppointment,
          promotion,
          giftCard,
          tipAmount,
        });
      }
      // TODO: CLEANING_V2 remove this when done in mobile-pro
      return computeCleaningPrices({
        universe,
        primaryAppointment,
        basketContent,
        tipAmount,
        zone,
        promotion,
        giftCard,
        duration,
      });
    default:
      return computeWellnessPrices({
        universe,
        zone,
        primaryAppointment,
        basketContent,
        comboPrices,
        minPriceSurchargeAccepted,
        tipAmount,
        promotion,
        giftCard,
        membershipComboPrices,
      });
  }
};

type CategoryPriceParams = {
  basketCategory: BasketContentItem;
  universe: Universe;
  zone: ZoneId | null;
  comboPrices: ComboPrice[];
  membershipComboPrices: ComboPrice[];
  shouldUseMembershipPrice?: boolean;
};

export const categoryPrice = ({
  basketCategory,
  universe,
  zone,
  comboPrices,
  membershipComboPrices,
  shouldUseMembershipPrice = false,
}: CategoryPriceParams): number => {
  const prices = basketContentPrices(basketCategory, universe, zone, comboPrices, membershipComboPrices);

  return shouldUseMembershipPrice && prices.price.membershipReduced !== undefined
    ? prices.price.membershipReduced
    : prices.price.reduced;
};

type GetTotalPriceToPayParams = {
  prices: BasketPrices;
  hasMembership: boolean;
};

// TODO_PRICES: remove cleaning condition when done in mobile
export const getTotalPriceToPay = ({ prices, hasMembership }: GetTotalPriceToPayParams): number => {
  if (prices.kind === 'wellness' && hasMembership) return prices.membership.totalPrice;
  if (prices.kind === 'cleaning' && hasMembership) return prices.membership.totalPrice;

  return prices.totalPrice;
};
